// Peakslog design tokens — jedyne źródło prawdy dla kolorów, typografii,
// spacingu, zaokrągleń i poświat w nowym layoucie FE.
// Wartości przepisane 1:1 z designu (artboardy + "Legenda systemu").
// Komponenty używają ich przez zmienne CSS (--pl-*, patrz theme/index.js)
// albo bezpośrednio z JS (SVG, canvas, karty share).

// ---------------------------------------------------------------------------
// Kolory
// ---------------------------------------------------------------------------

export const colors = {
  bg: {
    app: '#1B1A16',        // tło ekranu
    card: '#26241E',       // karta podniesiona (hero, odznaka, przycisk okrągły)
    sunken: '#211F1A',     // karta wtopiona (szybkie statystyki, mapa, wiersze legendy)
    raised: '#2E2B23',     // awatar
    locked: '#221F1A',     // kafelek odznaki niezaczętej
    track: '#33312A',      // tor pierścienia postępu (małe pierścienie)
    trackMuted: '#2A2822', // tor dużego pierścienia, tło ikony kłódki
    overlay: 'rgba(27,26,22,0.75)' // etykieta na mapie
  },
  text: {
    primary: '#F2EEE3',
    secondary: '#A8A398',
    muted: '#8B8779',      // podpisy, etykiety statystyk
    faint: '#706C60',      // stonowany (kamień), tekst odznaki zablokowanej
    dim: '#5C594F',        // najciemniejszy (np. "zablokowana", drobny druk)
    onAccent: '#1B1A16',   // tekst/ikona na przycisku w kolorze akcentu
    mossSoft: '#A8B89A'    // tekst w ramce informacyjnej w kolorze mchu
  },
  border: {
    subtle: 'rgba(242,238,227,0.08)',  // obwódka kart
    faint: 'rgba(242,238,227,0.06)',   // obwódka kart zablokowanych
    strong: 'rgba(242,238,227,0.1)',   // obwódka awatara / podglądu
    control: 'rgba(242,238,227,0.16)', // obwódka nieaktywnego chipa filtra (lista szczytów)
    divider: 'rgba(242,238,227,0.07)'  // separator listy
  },
  accent: {
    clay: '#C2703D',  // terakota — akcent główny, "w trakcie"
    moss: '#8A9A72',  // mech — "zdobyta", sukces
    gold: '#D9AE4E'   // szlachetny złoty — odznaki o wysokiej randze
  },
  // Kolory progów wysokości spoza akcentów (pozostałe progi = text.faint, accent.moss, accent.clay)
  elevation: {
    amber: '#D4A73D', // bursztyn 2000–2999 m
    ice: '#9FC9DC'    // lód 4000 m+
  },
  // Tinty akcentów (tła plakietek, ramek i poświat)
  tint: {
    clay14: 'rgba(194,112,61,0.14)',
    clay22: 'rgba(194,112,61,0.22)',
    clay55: 'rgba(194,112,61,0.55)',
    moss08: 'rgba(138,154,114,0.08)',
    moss10: 'rgba(138,154,114,0.1)',
    moss14: 'rgba(138,154,114,0.14)',
    moss30: 'rgba(138,154,114,0.3)',
    moss35: 'rgba(138,154,114,0.35)',
    moss55: 'rgba(138,154,114,0.55)',
    gold08: 'rgba(217,174,78,0.08)',
    gold30: 'rgba(217,174,78,0.3)'
  },
  // Dekoracyjne linie poziomic (stroke z opacity na warstwie SVG)
  contour: '#F2EEE3',
  // Karty share (Instagram Story)
  share: {
    clay: {
      background: 'linear-gradient(160deg,#20180F 0%,#1B1A16 45%,#171612 100%)',
      gradientStops: ['#20180F', '#1B1A16', '#171612'],
      ridgeBack: '#241E16',
      ridgeFront: '#1C1712'
    },
    moss: {
      background: 'linear-gradient(160deg,#1A1F16 0%,#1B1A16 45%,#17150F 100%)',
      gradientStops: ['#1A1F16', '#1B1A16', '#17150F'],
      ridgeBack: '#1F241A',
      ridgeFront: '#191D14'
    },
    divider: 'rgba(242,238,227,0.12)',
    dividerSoft: 'rgba(242,238,227,0.1)'
  },
  // Kolory marek zewnętrznych (logo Google w przycisku logowania)
  brand: {
    googleBlue: '#4285F4',
    googleGreen: '#34A853',
    googleYellow: '#FBBC05',
    googleRed: '#EA4335',
    strava: '#FC4C02'      // link "Zobacz w Stravie" (wytyczne marki Strava)
  },
  // Status (brak w designie — dobrane do palety, używane w stanach błędu)
  status: {
    error: '#D9785F',
    errorBg: 'rgba(217,120,95,0.1)',
    errorBorder: 'rgba(217,120,95,0.35)'
  }
}

// Mapa (MapLibre): podkład i markery w palecie Peakslog
colors.map = {
  land: colors.bg.app,
  landUrban: colors.bg.sunken,
  wood: 'rgba(138,154,114,0.12)',
  grass: 'rgba(138,154,114,0.06)',
  rock: 'rgba(242,238,227,0.03)',
  glacier: 'rgba(159,201,220,0.10)',
  water: '#1B2830',                   // jedyny nowy kolor: stonowany ciemny niebieski — morze i jeziora odcinają się od lądu
  waterLine: 'rgba(159,201,220,0.28)',
  road: 'rgba(242,238,227,0.10)',
  roadMajor: 'rgba(242,238,227,0.18)',
  trail: 'rgba(194,112,61,0.45)',     // szlaki (path) w terakocie
  rail: 'rgba(242,238,227,0.08)',
  building: 'rgba(242,238,227,0.04)',
  boundary: colors.text.dim,
  label: colors.text.muted,
  labelStrong: colors.text.secondary,
  labelHalo: colors.bg.app,
  hillshadeShadow: 'rgba(8,7,5,0.6)',
  hillshadeHighlight: 'rgba(242,238,227,0.10)',
  hillshadeAccent: 'rgba(194,112,61,0.06)',
  markerOutline: colors.bg.app,
  unclimbed: colors.text.dim,
  unclimbedFill: colors.bg.sunken,
  selected: colors.accent.clay,
  clusterFill: colors.bg.card,
  clusterBorder: colors.border.strong,
  clusterText: colors.text.primary,
  dimmed: 'rgba(27,26,22,0.55)',       // przygaszenie szczytów spoza korony (tryb wyzwania)
  hover: 'rgba(242,238,227,0.04)'      // podświetlenie klikalnej mini-mapy
}

// ---------------------------------------------------------------------------
// Motyw jasny — te same role co `colors`, ciepłe jasne tła z palety papieru.
// Akcenty przyciemnione, żeby tekst i linki miały kontrast ≥ 4.5:1 na tle.
// ---------------------------------------------------------------------------

export const lightColors = {
  bg: {
    app: '#F4F1EA',
    card: '#FCFBF8',
    sunken: '#ECE8DF',
    raised: '#E4DFD4',
    locked: '#EEEAE2',
    track: '#DFDACF',
    trackMuted: '#E6E1D7',
    overlay: 'rgba(244,241,234,0.86)'
  },
  text: {
    primary: '#1F1D18',
    secondary: '#524E45',
    muted: '#6B675C',
    faint: '#858073',
    dim: '#A29D90',
    onAccent: '#FFFFFF',
    mossSoft: '#4F5F38'
  },
  border: {
    subtle: 'rgba(31,29,24,0.10)',
    faint: 'rgba(31,29,24,0.07)',
    strong: 'rgba(31,29,24,0.14)',
    control: 'rgba(31,29,24,0.22)',
    divider: 'rgba(31,29,24,0.09)'
  },
  accent: {
    clay: '#A9582A',
    moss: '#5E7045',
    gold: '#8F6A12'
  },
  elevation: {
    amber: '#8A6A0F',
    ice: '#2E7398'
  },
  tint: {
    clay14: 'rgba(169,88,42,0.12)',
    clay22: 'rgba(169,88,42,0.18)',
    clay55: 'rgba(169,88,42,0.5)',
    moss08: 'rgba(94,112,69,0.08)',
    moss10: 'rgba(94,112,69,0.1)',
    moss14: 'rgba(94,112,69,0.14)',
    moss30: 'rgba(94,112,69,0.3)',
    moss35: 'rgba(94,112,69,0.35)',
    moss55: 'rgba(94,112,69,0.5)',
    gold08: 'rgba(143,106,18,0.08)',
    gold30: 'rgba(143,106,18,0.3)'
  },
  contour: '#1F1D18',
  share: colors.share, // karty share są zawsze ciemne (obraz do Stories)
  brand: colors.brand,
  status: {
    error: '#A8432A',
    errorBg: 'rgba(168,67,42,0.08)',
    errorBorder: 'rgba(168,67,42,0.3)'
  }
}

lightColors.map = {
  land: lightColors.bg.app,
  landUrban: lightColors.bg.sunken,
  wood: 'rgba(94,112,69,0.14)',
  grass: 'rgba(94,112,69,0.07)',
  rock: 'rgba(31,29,24,0.04)',
  glacier: 'rgba(46,115,152,0.10)',
  water: '#C9D9E0',                    // jasny, stonowany błękit
  waterLine: 'rgba(46,115,152,0.45)',
  road: 'rgba(31,29,24,0.12)',
  roadMajor: 'rgba(31,29,24,0.20)',
  trail: 'rgba(169,88,42,0.55)',
  rail: 'rgba(31,29,24,0.10)',
  building: 'rgba(31,29,24,0.06)',
  boundary: lightColors.text.faint,
  label: lightColors.text.muted,
  labelStrong: lightColors.text.secondary,
  labelHalo: lightColors.bg.app,
  hillshadeShadow: 'rgba(60,48,30,0.35)',
  hillshadeHighlight: 'rgba(255,255,255,0.55)',
  hillshadeAccent: 'rgba(169,88,42,0.06)',
  markerOutline: '#FFFFFF',
  unclimbed: lightColors.text.faint,
  unclimbedFill: lightColors.bg.card,
  selected: lightColors.accent.clay,
  clusterFill: lightColors.bg.card,
  clusterBorder: lightColors.border.control,
  clusterText: lightColors.text.primary,
  dimmed: 'rgba(244,241,234,0.55)',
  hover: 'rgba(31,29,24,0.04)'
}

// Palety kolorów per motyw (data-theme na <html>)
export const palettes = { dark: colors, light: lightColors }
export const THEMES = ['dark', 'light']

// ---------------------------------------------------------------------------
// Skala wysokości szczytu (5 progów) — "Legenda systemu"
// ---------------------------------------------------------------------------

// Zakresy progów; kolory per motyw w tierPalettes. W komponentach elevationTiers
// podaje kolory jako zmienne CSS (--pl-tier-*), więc same przełączają się z motywem.
const TIER_RANGES = [
  { key: 'stone', name: 'Kamień', min: 0, max: 999 },
  { key: 'moss', name: 'Mech', min: 1000, max: 1999 },
  { key: 'amber', name: 'Bursztyn', min: 2000, max: 2999 },
  { key: 'clay', name: 'Terakota', min: 3000, max: 3999 },
  { key: 'ice', name: 'Lód', min: 4000, max: Infinity }
]

// Surowe wartości progów (mapa MapLibre, generowanie zmiennych CSS)
export const tierPalettes = {
  dark: {
    stone: { color: colors.text.faint, background: 'rgba(112,108,96,0.14)', glow: null },
    moss: { color: colors.accent.moss, background: 'rgba(138,154,114,0.14)', glow: null },
    amber: { color: colors.elevation.amber, background: 'rgba(212,167,61,0.14)', glow: null },
    clay: { color: colors.accent.clay, background: 'rgba(194,112,61,0.14)', glow: null },
    ice: { color: colors.elevation.ice, background: 'rgba(159,201,220,0.16)', glow: '0 0 8px rgba(159,201,220,0.35)' } // tier 4000+ świeci
  },
  light: {
    stone: { color: '#77736A', background: 'rgba(119,115,106,0.14)', glow: null },
    moss: { color: lightColors.accent.moss, background: 'rgba(94,112,69,0.14)', glow: null },
    amber: { color: lightColors.elevation.amber, background: 'rgba(138,106,15,0.14)', glow: null },
    clay: { color: lightColors.accent.clay, background: 'rgba(169,88,42,0.12)', glow: null },
    ice: { color: lightColors.elevation.ice, background: 'rgba(46,115,152,0.12)', glow: '0 0 8px rgba(46,115,152,0.28)' }
  }
}

export const elevationTiers = TIER_RANGES.map(tier => ({
  ...tier,
  color: `var(--pl-tier-${tier.key}-color)`,
  background: `var(--pl-tier-${tier.key}-bg)`,
  glow: tierPalettes.dark[tier.key].glow ? `var(--pl-tier-${tier.key}-glow)` : null
}))

// Progi z surowymi kolorami danego motywu
export function tierValues(theme = 'dark') {
  const palette = tierPalettes[theme] || tierPalettes.dark
  return TIER_RANGES.map(tier => ({ ...tier, ...palette[tier.key] }))
}

// ---------------------------------------------------------------------------
// Stany odznaki — "Legenda systemu" (Stan odznaki + Waga odznaczenia)
// Wariant `regular` = terakota/mech, `noble` = szlachetny złoty.
// Każdy stan opisuje kafelek z logotypem (tile), kartę i kolory tekstu.
// ---------------------------------------------------------------------------

function buildBadgeStates(colors, glow) {
  return {
    regular: {
      notStarted: {
        tileBackground: colors.bg.locked,
        tileShadow: glow.notStarted,
        tileFilter: 'grayscale(1) opacity(0.55)', // logotyp wyszarzony
        iconColor: colors.text.secondary,
        cardBackground: colors.bg.sunken,
        cardBorder: colors.border.faint,
        titleColor: colors.text.faint,
        valueColor: colors.text.faint,
        labelColor: colors.text.dim,
        linkColor: colors.text.dim,
        ringColor: null
      },
      inProgress: {
        tileBackground: colors.bg.card,
        tileShadow: glow.inProgress, // cienka obwódka
        tileFilter: 'none',
        iconColor: colors.accent.clay,
        cardBackground: colors.bg.card,
        cardBorder: colors.border.subtle,
        titleColor: colors.text.primary,
        valueColor: colors.text.primary,
        labelColor: colors.text.muted,
        linkColor: colors.accent.clay,
        ringColor: colors.accent.clay
      },
      earned: {
        tileBackground: colors.bg.card,
        tileShadow: glow.earned, // świecąca obwódka
        tileFilter: 'none',
        iconColor: colors.accent.moss,
        cardBackground: colors.tint.moss08,
        cardBorder: colors.tint.moss30,
        titleColor: colors.text.primary,
        valueColor: colors.text.primary,
        labelColor: colors.accent.moss,
        linkColor: colors.accent.moss,
        ringColor: colors.accent.moss,
        checkBackground: colors.accent.moss // plakietka ✓
      }
    },
    noble: {
      notStarted: {
        tileBackground: colors.bg.locked,
        tileShadow: glow.nobleNotStarted, // przygaszona złota obwódka
        tileFilter: 'grayscale(1) opacity(0.55)',
        iconColor: colors.text.secondary,
        cardBackground: colors.bg.sunken,
        cardBorder: colors.border.faint,
        titleColor: colors.text.faint,
        valueColor: colors.text.faint,
        labelColor: colors.text.dim,
        linkColor: colors.text.dim,
        ringColor: null
      },
      inProgress: {
        tileBackground: colors.bg.card,
        tileShadow: glow.nobleInProgress,
        tileFilter: 'none',
        iconColor: colors.accent.gold,
        cardBackground: colors.bg.card,
        cardBorder: colors.border.subtle,
        titleColor: colors.text.primary,
        valueColor: colors.text.primary,
        labelColor: colors.text.muted,
        linkColor: colors.accent.gold,
        ringColor: colors.accent.gold
      },
      earned: {
        tileBackground: colors.bg.card,
        tileShadow: glow.nobleEarned,
        tileFilter: 'none',
        iconColor: colors.accent.gold,
        // Karta zdobytej szlachetnej: brak w designie — analogicznie do zdobytej zwykłej, w złocie
        cardBackground: colors.tint.gold08,
        cardBorder: colors.tint.gold30,
        titleColor: colors.text.primary,
        valueColor: colors.text.primary,
        labelColor: colors.accent.gold,
        linkColor: colors.accent.gold,
        ringColor: colors.accent.gold,
        checkBackground: colors.accent.gold
      }
    }
  }
}

const BADGE_GLOW_DARK = {
  notStarted: 'inset 0 0 0 1px rgba(242,238,227,0.08)',
  inProgress: '0 0 0 2px rgba(194,112,61,0.55)',
  earned: '0 0 0 2px #8A9A72, 0 0 16px 2px rgba(138,154,114,0.55)',
  nobleNotStarted: 'inset 0 0 0 1.5px rgba(217,174,78,0.4)',
  nobleInProgress: '0 0 0 2px rgba(217,174,78,0.7), 0 0 16px 3px rgba(217,174,78,0.35)',
  nobleEarned: '0 0 0 2px #D9AE4E, 0 0 22px 5px rgba(217,174,78,0.55), 0 0 40px 8px rgba(217,174,78,0.22)'
}

const BADGE_GLOW_LIGHT = {
  notStarted: 'inset 0 0 0 1px rgba(31,29,24,0.10)',
  inProgress: '0 0 0 2px rgba(169,88,42,0.55)',
  earned: '0 0 0 2px #5E7045, 0 0 14px 2px rgba(94,112,69,0.35)',
  nobleNotStarted: 'inset 0 0 0 1.5px rgba(143,106,18,0.4)',
  nobleInProgress: '0 0 0 2px rgba(176,134,40,0.75), 0 0 14px 3px rgba(217,174,78,0.35)',
  nobleEarned: '0 0 0 2px #B08628, 0 0 20px 5px rgba(217,174,78,0.45), 0 0 36px 8px rgba(217,174,78,0.2)'
}

// Surowe wartości stanów odznak per motyw (zmienne --pl-badge-*)
export const badgeStatePalettes = {
  dark: buildBadgeStates(colors, BADGE_GLOW_DARK),
  light: buildBadgeStates(lightColors, BADGE_GLOW_LIGHT)
}

// ---------------------------------------------------------------------------
// Typografia
// ---------------------------------------------------------------------------

export const fonts = {
  display: "'Barlow Condensed', 'Arial Narrow', sans-serif", // nagłówki i liczby
  body: "'IBM Plex Sans', system-ui, sans-serif"             // treść
}

// Rozmiary w px; family: 'display' | 'body'
export const typeScale = {
  // ekrany aplikacji (mobile 390)
  heroNumber: { family: 'display', size: 80, weight: 800, lineHeight: 0.9 },
  screenTitle: { family: 'display', size: 28, weight: 800, lineHeight: 1.15 },
  screenTitleLarge: { family: 'display', size: 34, weight: 800, lineHeight: 1.1 }, // tytuł ekranu na desktopie (Wszystkie szczyty)
  yearTitle: { family: 'display', size: 18, weight: 700, lineHeight: 1.2 },        // nagłówek rocznika (lista szczytów)
  yearTitleSmall: { family: 'display', size: 16, weight: 700, lineHeight: 1.2 },   // rocznik w tabeli (desktop)
  authTitle: { family: 'display', size: 27, weight: 700, lineHeight: 1.15 },
  authTitleSmall: { family: 'display', size: 24, weight: 700, lineHeight: 1.2 },
  legendTitle: { family: 'display', size: 24, weight: 800, lineHeight: 1.15 },
  wordmark: { family: 'display', size: 21, weight: 800, lineHeight: 1, letterSpacing: 1 },
  wordmarkLarge: { family: 'display', size: 25, weight: 800, lineHeight: 1, letterSpacing: 1 },
  sectionTitle: { family: 'display', size: 20, weight: 700, lineHeight: 1.2 },
  statValue: { family: 'display', size: 24, weight: 700, lineHeight: 1.2 },
  statValueLarge: { family: 'display', size: 44, weight: 700, lineHeight: 1.1 },
  badgeValue: { family: 'display', size: 20, weight: 700, lineHeight: 1.2 },
  pill: { family: 'display', size: 13, weight: 700, lineHeight: 1.2 },
  avatar: { family: 'display', size: 13, weight: 700, lineHeight: 1 },
  heroUnit: { family: 'body', size: 19, weight: 500, lineHeight: 1.3 },
  button: { family: 'body', size: 15, weight: 700, lineHeight: 1.45 },
  itemTitle: { family: 'body', size: 15, weight: 600, lineHeight: 1.4 },
  body: { family: 'body', size: 14, weight: 400, lineHeight: 1.5 },
  bodyStrong: { family: 'body', size: 14, weight: 600, lineHeight: 1.3 },
  small: { family: 'body', size: 13, weight: 400, lineHeight: 1.45 },
  smallMedium: { family: 'body', size: 13, weight: 500, lineHeight: 1.45 },
  smallStrong: { family: 'body', size: 13, weight: 600, lineHeight: 1.25 },
  caption: { family: 'body', size: 12, weight: 400, lineHeight: 1.45 },
  captionStrong: { family: 'body', size: 12, weight: 600, lineHeight: 1.45 },
  overline: { family: 'body', size: 11, weight: 600, lineHeight: 1.4, letterSpacing: 1.5, uppercase: true },
  tableHead: { family: 'body', size: 11, weight: 600, lineHeight: 1.4, letterSpacing: 1, uppercase: true },
  sectionLabel: { family: 'body', size: 12, weight: 600, lineHeight: 1.4, letterSpacing: 1, uppercase: true },
  micro: { family: 'body', size: 10, weight: 400, lineHeight: 1.4, letterSpacing: 1, uppercase: true },
  badgeLabel: { family: 'body', size: 10, weight: 400, lineHeight: 1.4 },
  badgeLabelStrong: { family: 'body', size: 10, weight: 600, lineHeight: 1.4 },
  fine: { family: 'body', size: 11, weight: 400, lineHeight: 1.6 },
  tiny: { family: 'body', size: 11, weight: 400, lineHeight: 1.4 },
  note: { family: 'body', size: 12.5, weight: 400, lineHeight: 1.55 },
  tinyStrong: { family: 'body', size: 11, weight: 600, lineHeight: 1.3 },
  mapAttribution: { family: 'body', size: 9, weight: 400, lineHeight: 1.5 },

  // strona główna (desktop 1280)
  landingHero: { family: 'display', size: 64, weight: 800, lineHeight: 1.02 },
  landingHeroMobile: { family: 'display', size: 44, weight: 800, lineHeight: 1.05 },
  landingPreviewNumber: { family: 'display', size: 96, weight: 800, lineHeight: 0.85 },
  landingPreviewLabel: { family: 'body', size: 10, weight: 600, lineHeight: 1.4, letterSpacing: 2, uppercase: true },
  landingChip: { family: 'body', size: 12, weight: 700, lineHeight: 1.3 },
  landingOverline: { family: 'body', size: 13, weight: 700, lineHeight: 1.4, letterSpacing: 2, uppercase: true },
  landingLead: { family: 'body', size: 17, weight: 400, lineHeight: 1.6 },
  landingSection: { family: 'display', size: 28, weight: 700, lineHeight: 1.2 },
  landingFeature: { family: 'display', size: 22, weight: 700, lineHeight: 1.2 },
  landingStep: { family: 'display', size: 40, weight: 800, lineHeight: 1 },
  landingStepTitle: { family: 'body', size: 16, weight: 600, lineHeight: 1.4 },
  landingNav: { family: 'body', size: 15, weight: 500, lineHeight: 1.4 },
  landingNavStrong: { family: 'body', size: 15, weight: 600, lineHeight: 1.4 },
  landingBody: { family: 'body', size: 14, weight: 400, lineHeight: 1.6 },
  landingWordmark: { family: 'display', size: 23, weight: 800, lineHeight: 1, letterSpacing: 1 }
}

// Skala kart share (płótno 1080×1920)
export const shareTypeScale = {
  heroNumber: { family: 'display', size: 340, weight: 800, lineHeight: 0.85 },
  crownName: { family: 'display', size: 110, weight: 800, lineHeight: 1 },
  challengeName: { family: 'display', size: 64, weight: 800, lineHeight: 1.1 },
  ringValue: { family: 'display', size: 88, weight: 800, lineHeight: 1 },
  heroUnit: { family: 'body', size: 38, weight: 500, lineHeight: 1.3 },
  subtitle: { family: 'body', size: 30, weight: 500, lineHeight: 1.3 },
  subtitleSmall: { family: 'body', size: 26, weight: 500, lineHeight: 1.3 },
  ringUnit: { family: 'body', size: 22, weight: 500, lineHeight: 1.3 },
  overline: { family: 'body', size: 26, weight: 700, lineHeight: 1.3, letterSpacing: 4, uppercase: true },
  statValue: { family: 'display', size: 46, weight: 700, lineHeight: 1.2 },
  statValueSmall: { family: 'display', size: 40, weight: 700, lineHeight: 1.2 },
  statLabel: { family: 'body', size: 18, weight: 400, lineHeight: 1.3, letterSpacing: 1.5, uppercase: true },
  statLabelSmall: { family: 'body', size: 16, weight: 400, lineHeight: 1.3, letterSpacing: 1.5, uppercase: true },
  peakName: { family: 'body', size: 34, weight: 600, lineHeight: 1.3 },
  peakMeta: { family: 'body', size: 20, weight: 400, lineHeight: 1.3 },
  peakElevation: { family: 'display', size: 36, weight: 700, lineHeight: 1.2 },
  listName: { family: 'body', size: 30, weight: 600, lineHeight: 1.3 },
  listElevation: { family: 'display', size: 28, weight: 700, lineHeight: 1.2 },
  listNameSmall: { family: 'body', size: 26, weight: 600, lineHeight: 1.3 },
  listElevationSmall: { family: 'display', size: 24, weight: 700, lineHeight: 1.2 },
  chip: { family: 'body', size: 28, weight: 700, lineHeight: 1.3 },
  wordmark: { family: 'display', size: 26, weight: 800, lineHeight: 1, letterSpacing: 1.5 },
  handle: { family: 'body', size: 28, weight: 600, lineHeight: 1.3 },
  footnote: { family: 'body', size: 22, weight: 400, lineHeight: 1.3 }
}

// ---------------------------------------------------------------------------
// Zaokrąglenia, spacing, cienie/poświaty
// ---------------------------------------------------------------------------

export const radii = {
  pill: 999,     // plakietki, awatary, okrągłe przyciski
  hero: 20,      // karta hero, kafelki funkcji na landingu
  map: 18,
  card: 16,      // karty odznak, kafelek logotypu 56px
  control: 14,   // przyciski CTA, szybkie statystyki, ramki info
  tile: 12,      // kafelek logotypu 40px
  row: 10,       // wiersze legendy
  label: 8,      // etykieta na mapie, pozycja filtra w panelu
  checkbox: 4,   // pole wyboru filtra
  swatch: 3,     // próbka koloru progu wysokości
  device: 32     // podgląd telefonu na landingu
}

export const space = {
  pageX: 20,          // boczny padding ekranu mobile
  pageTop: 24,
  pageBottom: 32,
  section: 28,        // odstęp między sekcjami dashboardu
  sectionInner: 14,   // tytuł sekcji → treść
  stack: 10,
  gridTight: 10,      // szybkie statystyki
  gridBadges: 14,     // siatka odznak
  scroller: 12,       // karuzela odznak
  cardPadding: 16,
  cardPaddingSmall: 14,
  heroPadding: 24,
  listRowY: 13,
  landingX: 72,
  landingSection: 96,
  landingGrid: 24,
  landingCard: 32,
  sharePageX: 88,
  sharePageY: 80,
  // Desktop (≥ breakpoints.desktopLayout)
  desktopX: 48,
  desktopTop: 32,
  desktopBottom: 64,
  desktopGap: 24,
  contentMax: 1200,
  topbarHeight: 81,   // górny pasek desktopu (z dolną linią 1 px)
  mapPanel: 360,      // panel boczny mapy na desktopie
  filterSidebar: 240, // panel filtrów listy szczytów na desktopie
  sidebarGap: 40      // odstęp panelu filtrów od tabeli
}

// Markery mapy (wartości liczbowe; kolory są w colors.map)
export const mapMarkers = {
  dimmedOpacity: 0.28 // szczyty spoza korony w trybie wyzwania
}

export const shadows = {
  mapDot: '0 0 0 4px rgba(194,112,61,0.22)',
  device: '0 40px 80px rgba(0,0,0,0.45)',
  iceGlow: '0 0 8px rgba(159,201,220,0.35)',
  focusRing: '0 0 0 2px #1B1A16, 0 0 0 4px #C2703D'
}

export const shadowPalettes = {
  dark: shadows,
  light: {
    mapDot: '0 0 0 4px rgba(169,88,42,0.2)',
    device: '0 30px 60px rgba(60,48,30,0.18)',
    iceGlow: '0 0 8px rgba(46,115,152,0.28)',
    focusRing: '0 0 0 2px #F4F1EA, 0 0 0 4px #A9582A'
  }
}

// Pierścień postępu na karcie share (SVG): poświata złota/terakota
export const progressRing = {
  trackWidth: 14,
  glowWidth: 20,
  glowBlur: 9,
  glowOpacity: 0.55
}

export const breakpoints = {
  mobile: 390,
  tablet: 768,
  desktopLayout: 1024, // od tej szerokości ekrany aplikacji mają układ desktopowy (media queries: 1024px)
  desktop: 1280
}

const tokens = {
  colors,
  lightColors,
  palettes,
  elevationTiers,
  tierPalettes,
  badgeStatePalettes,
  fonts,
  typeScale,
  shareTypeScale,
  radii,
  space,
  shadows,
  shadowPalettes,
  mapMarkers,
  progressRing,
  breakpoints
}

export default tokens
